import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { suppliersApi } from '../api/suppliers.api';
import { Supplier } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const emptySupplier: Partial<Supplier> = { name: '', phone: '', email: '', address: '' };

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Partial<Supplier>>(emptySupplier);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);

  const loadSuppliers = async () => {
    try {
      const res = await suppliersApi.getAll();
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSuppliers(); }, []);

  const handleOpenCreate = () => {
    setSelected(emptySupplier);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (sup: Supplier) => {
    setSelected({ ...sup });
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditing && selected.id) {
        await suppliersApi.update(selected.id, selected);
      } else {
        await suppliersApi.create(selected);
      }
      setModalOpen(false);
      loadSuppliers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await suppliersApi.delete(deleteId);
      setDeleteDialogOpen(false);
      loadSuppliers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (field: string, value: string) => {
    setSelected((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Proveedores</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nuevo Proveedor
        </Button>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'name', label: 'Nombre', sx: { fontWeight: 500 } },
          { id: 'phone', label: 'Telefono' },
          { id: 'email', label: 'Email' },
          { id: 'address', label: 'Direccion' },
        ]}
        rows={suppliers}
        searchFields={['name', 'email', 'phone']}
        exportFileName="proveedores"
        emptyMessage="No hay proveedores registrados"
        renderActions={(s) => (
          <>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(s)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(s.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                fullWidth label="Nombre" value={selected.name || ''}
                onChange={(e) => handleChange('name', e.target.value)} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth label="Telefono" value={selected.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth label="Email" type="email" value={selected.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth label="Direccion" value={selected.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                multiline rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Proveedor"
        message="¿Estas seguro de que deseas eliminar este proveedor?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Suppliers;
